import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useMyScholarships, useApplyScholarship } from '@/hooks';
import { motion } from 'framer-motion';
import PageHeader from '@/components/common/PageHeader';
import EmptyState from '@/components/common/EmptyState';
import {
  Heart,
  Star,
  BookOpen,
  Users,
  Crown,
  Send,
  Loader2,
  CheckCircle2,
  Clock,
  XCircle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { SEO, createBreadcrumbSchema } from '@/components/seo';

const SCHOLARSHIP_TYPES = [
  { type: 'Need-Based', icon: Heart, color: 'text-red-600 bg-red-100 dark:bg-red-900/40 dark:text-red-400', descEn: 'For students with financial constraints', descAr: 'للطلاب ذوي القيود المالية' },
  { type: 'Merit-Based', icon: Star, color: 'text-amber-600 bg-amber-100 dark:bg-amber-900/40 dark:text-amber-400', descEn: 'For high-achieving students', descAr: 'للطلاب المتفوقين' },
  { type: 'Hafiz Program', icon: BookOpen, color: 'text-emerald-600 bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-400', descEn: 'For dedicated Hifz students', descAr: 'لطلاب الحفظ المتفانين' },
  { type: 'Community Service', icon: Users, color: 'text-blue-600 bg-blue-100 dark:bg-blue-900/40 dark:text-blue-400', descEn: 'For community contributors', descAr: 'للمساهمين في المجتمع' },
  { type: 'Full Scholarship', icon: Crown, color: 'text-purple-600 bg-purple-100 dark:bg-purple-900/40 dark:text-purple-400', descEn: 'Full coverage scholarship', descAr: 'منحة تغطية كاملة' },
];

const STATUS_STYLES: Record<string, { color: string; icon: typeof Clock }> = {
  Pending: { color: 'text-amber-600 border-amber-200 dark:border-amber-800', icon: Clock },
  Approved: { color: 'text-emerald-600 border-emerald-200 dark:border-emerald-800', icon: CheckCircle2 },
  Rejected: { color: 'text-red-600 border-red-200 dark:border-red-800', icon: XCircle },
};

const Scholarships = () => {
  const { isRTL } = useLanguage();
  const { isAuthenticated } = useAuth();
  const { data: myScholarships } = useMyScholarships();
  const applyScholarship = useApplyScholarship();

  const [showApply, setShowApply] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [reason, setReason] = useState('');
  const [income, setIncome] = useState('');
  const [dependents, setDependents] = useState('');

  const applications = (myScholarships as any[]) || [];

  const handleApply = async () => {
    if (!selectedType || !reason.trim()) return;
    try {
      await applyScholarship.mutateAsync({
        scholarship_type: selectedType,
        reason,
        monthly_income: income ? parseFloat(income) : undefined,
        dependents: dependents ? parseInt(dependents) : undefined,
      });
      toast.success(isRTL ? 'تم تقديم الطلب بنجاح' : 'Application submitted successfully');
      setShowApply(false);
      setReason('');
      setIncome('');
      setDependents('');
    } catch {
      toast.error(isRTL ? 'فشل تقديم الطلب' : 'Failed to submit application');
    }
  };

  const lang = isRTL ? 'ar' : 'en';
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      createBreadcrumbSchema(
        [
          { name: isRTL ? 'الرئيسية' : 'Home', url: 'https://hudanlilmuttaqeen.com/' },
          { name: isRTL ? 'المنح الدراسية' : 'Scholarships', url: 'https://hudanlilmuttaqeen.com/scholarships' },
        ],
        lang
      ),
    ],
  };

  return (
    <Layout>
      <SEO
        title={isRTL ? 'المنح الدراسية | هُدًى للمتقين' : 'Scholarships | Hudan Lil Muttaqeen'}
        description={isRTL
          ? 'تعرّف على برامج المنح الدراسية المتاحة لتعلم القرآن الكريم على منصة هُدًى للمتقين.'
          : 'Discover scholarship programmes for Quran learning with Hudan Lil Muttaqeen.'}
        structuredData={structuredData}
      />
      <PageHeader
        title="Scholarships"
        titleAr="المنح الدراسية"
        subtitle="Financial support for your Quran learning journey"
        subtitleAr="دعم مالي لرحلة تعلم القرآن الكريم"
        size="sm"
      />

      <div className="container py-8 space-y-8" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Scholarship Types */}
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-4">
            {isRTL ? 'أنواع المنح المتاحة' : 'Available Scholarship Types'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SCHOLARSHIP_TYPES.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.type}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Card className="border-border hover:border-primary/20 transition-all hover:shadow-md h-full">
                    <CardContent className="pt-5 pb-5 flex flex-col h-full">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${s.color}`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <h3 className="text-sm font-semibold text-foreground mb-1">{s.type}</h3>
                      <p className="text-xs text-muted-foreground mb-4 flex-1">{isRTL ? s.descAr : s.descEn}</p>
                      {isAuthenticated ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setSelectedType(s.type); setShowApply(true); }}
                          className="w-full gap-1.5"
                        >
                          <Send className="h-3.5 w-3.5" />
                          {isRTL ? 'تقديم طلب' : 'Apply'}
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" asChild className="w-full">
                          <Link to="/login">{isRTL ? 'سجل الدخول للتقديم' : 'Login to Apply'}</Link>
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* My Applications */}
        {isAuthenticated && (
          <div>
            <h2 className="text-lg font-semibold text-foreground mb-4">
              {isRTL ? 'طلباتي' : 'My Applications'}
            </h2>
            {applications.length > 0 ? (
              <div className="space-y-3">
                {applications.map((app: any, i: number) => {
                  const statusStyle = STATUS_STYLES[app.status] || STATUS_STYLES.Pending;
                  const StatusIcon = statusStyle.icon;
                  return (
                    <motion.div
                      key={app.name}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                    >
                      <Card className="border-border">
                        <CardContent className="pt-4 pb-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                                {SCHOLARSHIP_TYPES.find(s => s.type === app.scholarship_type)?.icon &&
                                  React.createElement(
                                    SCHOLARSHIP_TYPES.find(s => s.type === app.scholarship_type)!.icon,
                                    { className: 'h-5 w-5 text-muted-foreground' }
                                  )
                                }
                              </div>
                              <div>
                                <p className="text-sm font-medium text-foreground">{app.scholarship_type}</p>
                                <p className="text-xs text-muted-foreground">{app.creation?.split(' ')[0]}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {app.approved_amount && (
                                <span className="text-sm font-semibold text-emerald-600">${app.approved_amount}</span>
                              )}
                              <Badge variant="outline" className={`gap-1 ${statusStyle.color}`}>
                                <StatusIcon className="h-3 w-3" />
                                {app.status}
                              </Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <EmptyState
                icon={Heart}
                title={isRTL ? 'لا توجد طلبات' : 'No applications yet'}
                description={isRTL ? 'قدم طلبك للحصول على منحة' : 'Apply for a scholarship above'}
                className="py-8"
              />
            )}
          </div>
        )}
      </div>

      {/* Apply Dialog */}
      <Dialog open={showApply} onOpenChange={setShowApply}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'تقديم طلب منحة' : 'Apply for Scholarship'}</DialogTitle>
            <DialogDescription>{selectedType}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="scholarship-reason">{isRTL ? 'سبب التقديم' : 'Reason / Personal Statement'} *</Label>
              <Textarea
                id="scholarship-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={isRTL ? 'اشرح سبب تقديمك لهذه المنحة...' : 'Explain why you are applying for this scholarship...'}
                className="resize-none h-24"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="scholarship-income">{isRTL ? 'الدخل الشهري (اختياري)' : 'Monthly Income (optional)'}</Label>
                <Input
                  id="scholarship-income"
                  type="number"
                  value={income}
                  onChange={(e) => setIncome(e.target.value)}
                  placeholder="$0"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="scholarship-dependents">{isRTL ? 'عدد المعالين' : 'Dependents'}</Label>
                <Input
                  id="scholarship-dependents"
                  type="number"
                  value={dependents}
                  onChange={(e) => setDependents(e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowApply(false)}>
                {isRTL ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={handleApply}
                disabled={!reason.trim() || applyScholarship.isPending}
              >
                {applyScholarship.isPending ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
                {isRTL ? 'تقديم' : 'Submit'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Scholarships;
